import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle,
  Cancel,
  HelpOutline,
} from '@mui/icons-material';
import { sispService } from '../../../../services/sisp.service';

interface TransactionResult {
  result: boolean;
  msg: string;
  transactionSuccess: boolean;
  transactionStatusDescription: string;
}

const TransactionStatus: React.FC = () => {
  const [merchantRef, setMerchantRef] = useState('');
  const [posId, setPosId] = useState('');
  const [posAuthCode, setPosAuthCode] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TransactionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleQuery = async () => {
    if (!merchantRef.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await sispService.queryTransactionStatus({
        merchantRef: merchantRef.trim(),
        posId: posId.trim() || undefined,
        posAuthCode: posAuthCode.trim() || undefined,
        environment,
      });

      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError(response.error || 'Erro ao consultar estado da transacao');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao consultar estado da transacao');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Consultar Estado de Transacao SISP
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, md: showAdvanced ? 3 : 5 }}>
              <TextField
                fullWidth
                label="Referencia (merchantRef)"
                value={merchantRef}
                onChange={(e) => setMerchantRef(e.target.value)}
                placeholder="Ex: 300320261430ABC"
                inputProps={{ style: { fontFamily: 'monospace' } }}
                required
              />
            </Grid>
            {showAdvanced && (
              <>
                <Grid size={{ xs: 12, md: 2 }}>
                  <TextField
                    fullWidth
                    label="POS ID"
                    value={posId}
                    onChange={(e) => setPosId(e.target.value)}
                    placeholder="Forçar POS"
                    helperText="Vazio = automático"
                    autoComplete="off"
                    inputProps={{ style: { fontFamily: 'monospace' }, autoComplete: 'off' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                  <TextField
                    fullWidth
                    label="POS Auth Code"
                    value={posAuthCode}
                    onChange={(e) => setPosAuthCode(e.target.value)}
                    placeholder="Forçar POS"
                    type="password"
                    helperText="Vazio = automático"
                    autoComplete="new-password"
                    inputProps={{ autoComplete: 'new-password' }}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, md: showAdvanced ? 2 : 3 }}>
              <TextField
                fullWidth
                select
                label="Ambiente"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'production')}
              >
                <MenuItem value="sandbox">Sandbox</MenuItem>
                <MenuItem value="production">Producao</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: showAdvanced ? 3 : 4 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleQuery}
                disabled={loading || !merchantRef.trim()}
                sx={{ height: 56 }}
              >
                Consultar
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              O POS e as credenciais do portal são resolvidos automaticamente (do pagamento e do ambiente) — não precisas de preencher nada além da referência.
            </Typography>
            <Button
              size="small"
              onClick={() => { if (showAdvanced) { setPosId(''); setPosAuthCode(''); } setShowAdvanced(v => !v); }}
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              {showAdvanced ? 'Ocultar opções avançadas' : 'Forçar POS (avançado)'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              {result.transactionSuccess ? (
                <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
              ) : result.result ? (
                <Cancel sx={{ fontSize: 48, color: 'error.main' }} />
              ) : (
                <HelpOutline sx={{ fontSize: 48, color: 'warning.main' }} />
              )}
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {result.transactionSuccess
                    ? 'Transacao Aprovada'
                    : result.result
                      ? 'Transacao Nao Aprovada'
                      : 'Consulta sem resultado'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {result.msg}
                </Typography>
              </Box>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 250 }}>Referencia</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{merchantRef}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Consulta executada</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={result.result ? 'Sim' : 'Nao'}
                        color={result.result ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Pagamento aprovado</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={result.transactionSuccess ? 'Sim' : 'Nao'}
                        color={result.transactionSuccess ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {result.transactionStatusDescription || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Mensagem</TableCell>
                    <TableCell>{result.msg || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Ambiente</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={environment === 'sandbox' ? 'Sandbox' : 'Producao'}
                        color={environment === 'sandbox' ? 'info' : 'success'}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {!result && !error && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            Insira a referencia (merchantRef) para consultar o estado da transacao na SISP.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            API: comerciante.teste.sisp.cv/pos/transaction-status (Sandbox) | comerciante.vinti4.cv/pos/transaction-status (Producao)
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TransactionStatus;
